import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { format } from "@gamedev-web/gamedev-sdk-core";

import { useStrings } from "../../localization/localizationHelpers";
import { CrmGameConceptStatus } from "../../models/crmStatus";
import { AaaGameConceptData } from "../../models/forms";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { getOnboardingSignedIn } from "../../redux/selectors/authSelectors";
import { GameConceptState } from "../../server/crm/api/models/forms";
import { FormPreview } from "./FormPreview";
import { FormPreviewValidDataType, ReadOnlyFormId } from "./ReadOnlyForms";

export type ApplicationInformationSectionProps = {
  gameConceptInfo?: AaaGameConceptData & GameConceptState;
};

export const AaaGameConceptFormPreview = ({
  gameConceptInfo,
}: ApplicationInformationSectionProps) => {
  const [
    welcomePartnerPageApplicationName,
    welcomePartnerPageApplicationStatusApproved,
    welcomePartnerPageApplicationStatusDenied,
    untitledGame,
  ] = useStrings(
    "welcomePartnerPageApplicationName",
    "welcomePartnerPageApplicationStatusApproved",
    "welcomePartnerPageApplicationStatusDenied",
    "untitledGame"
  );

  const isSignedIn = useSelector(getOnboardingSignedIn);
  const history = useHistory();

  useEffect(() => {
    if (!isSignedIn) {
      history.replace(OnboardingUrls[RouteNames.AaaWelcome]);
      return;
    }
  }, [isSignedIn, history]);

  if (!gameConceptInfo) {
    return null;
  }

  const {
    details,
    distributionInformation,
    gameExpectations,
    availability,
    technicalCapabilities,
    configurations,
    xboxNetwork,
    ugc,
    supplementaryData,
    state: gameState,
  } = gameConceptInfo ?? {};

  const readOnlyForms = new Map<ReadOnlyFormId, FormPreviewValidDataType>([
    [ReadOnlyFormId.AaaConceptDetails, details],
    [ReadOnlyFormId.AaaDistributionInformation, distributionInformation],
    [ReadOnlyFormId.AaaGameExpectations, gameExpectations],
    [ReadOnlyFormId.AaaAvailability, availability],
    [ReadOnlyFormId.AaaTechnicalCapabilities, technicalCapabilities],
    [ReadOnlyFormId.AaaConfigurations, configurations],
    [ReadOnlyFormId.AaaXboxNetwork, xboxNetwork],
    [ReadOnlyFormId.AaaUgc, ugc],
    [ReadOnlyFormId.AaaSupplementaryData, supplementaryData],
  ]);

  let subtitle = "";
  switch (gameState?.status) {
    case CrmGameConceptStatus.Accepted:
      subtitle = welcomePartnerPageApplicationStatusApproved;
      break;
    case CrmGameConceptStatus.Rejected:
      subtitle = welcomePartnerPageApplicationStatusDenied;
      break;
  }

  return (
    <FormPreview
      title={format(welcomePartnerPageApplicationName, details?.gameTitle ?? untitledGame)}
      subtitle={subtitle}
      readOnlyForms={readOnlyForms}
    />
  );
};
